import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import PageSection from 'components/PageSection';
import TenantsContainer from 'containers/Tenants/TenantsContainer';
import UsersContainer from 'containers/Users/UsersContainer';
import SystemBaseUrl from 'containers/General/BaseUrl';
import AllowSignup from 'containers/General/AllowSignup';
import AccountsContainer from 'containers/Accounts/AccountsContainer';
import AuthSettingsContainer from 'containers/AuthSettings/AuthSettingsContainer';
import RolesSettingsContainer from 'containers/Roles/RolesSettingsContainer';
import SystemLicensingPage from 'containers/Licensing/SystemLicensingPage';
import TasksContainer from 'containers/Tasks';
import EmailSettingsContainer from 'containers/EmailSettings/EmailSettingsContainer';
import FirewallsContainer from 'containers/Firewalls/FirewallsContainer';
import WorkspacesContainer from 'containers/Workspaces/WorkspacesContainer';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';
import SystemReset from './SystemReset';
import WorkspaceFirewallSettings from './WorkspaceSettings/WorkspaceFirewallSettings';
import WorkspacePolicySettings from './WorkspaceSettings/WorkspacePolicySettings';
import WorkspaceCategorySettings from './WorkspaceSettings/WorkspaceCategorySettings';
import WorkspaceUserGroupSettings from './WorkspaceSettings/WorkspaceUserGroupSettings';

export default function Settings() {
	const ctx = useOrgContext();

	return (
		<div>
			<PageSection title="Settings" level={2} />

			<div className="settings-container">
				<nav className="settings-nav-container">
					{ctx?.isInWorkspace && (
						<React.Fragment>
							<div><OrgContextLink level="tenant" to={`/settings`}>&#x25B2; {ctx?.tenant?.name ?? 'Tenant'} Settings</OrgContextLink></div>

							<div className="settings-nav-group-header">{ctx?.workspace?.name ?? 'Workspace'}<br /><span style={{ fontSize: '60%' }}>Workspace</span></div>

							<ul className="settings-nav-list">
								<li><OrgContextLink level="workspace" to={`/settings/users`}>Users</OrgContextLink></li>
								<li><OrgContextLink level="workspace" to={`/settings/workspace-firewall`}>Firewall</OrgContextLink></li>
								<li><OrgContextLink level="workspace" to={`/settings/workspace-policies`}>Policies</OrgContextLink></li>
								<li><OrgContextLink level="workspace" to={`/settings/workspace-categories`}>Categories</OrgContextLink></li>
								<li><OrgContextLink level="workspace" to={`/settings/workspace-usergroups`}>User Groups</OrgContextLink></li>
								<li><OrgContextLink level="workspace" to={`/settings/tasks`}>Tasks</OrgContextLink></li>
							</ul>
						</React.Fragment>
					)}

					{(ctx?.isInTenant && !ctx?.isInWorkspace) && (
						<React.Fragment>
							<div className="settings-nav-group-header">{ctx?.tenant?.name ?? 'Tenant'}<br /><span style={{ fontSize: '60%' }}>Tenant</span></div>

							<ul className="settings-nav-list">
								<li><OrgContextLink level="tenant" to={`/settings/workspaces`}>Workspaces</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/auth`}>Authentication</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/roles`}>Roles</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/email`}>Email</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/firewalls`}>Firewalls</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/users`}>Users</OrgContextLink></li>
								<li><OrgContextLink level="tenant" to={`/settings/tasks`}>Tasks</OrgContextLink></li>
							</ul>
						</React.Fragment>
					)}

					{ctx?.isInSystem && (
						<React.Fragment>
							<div className="settings-nav-group-header">System</div>

							<ul className="settings-nav-list">
								<li><OrgContextLink level="system" to={`/settings/general`}>General</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/accounts`}>Accounts</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/tenants`}>Tenants</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/workspaces`}>Workspaces</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/auth`}>Authentication</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/roles`}>Roles</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/email`}>Email</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/licensing`}>Licensing</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/users`}>Users</OrgContextLink></li>
								<li><OrgContextLink level="system" to={`/settings/tasks`}>Tasks</OrgContextLink></li>
							</ul>
						</React.Fragment>
					)}

					<React.Fragment>
						<div className="settings-nav-group-header">Other</div>
						<ul className="settings-nav-list">
							<li><OrgContextLink to={`/settings/accounts`}>Accounts</OrgContextLink></li>
							<li><OrgContextLink to={`/settings/tenants`}>Tenants</OrgContextLink></li>
						</ul>
					</React.Fragment>
				</nav>
				<main className="settings-content-container">
					<Routes>
						<Route path="/" element={
							<div>
							</div>
						} />

						{ctx?.isInWorkspace && (
							<React.Fragment>
								<Route path="/users/*" element={<UsersContainer />} />
								<Route path="/workspace-firewall" element={<WorkspaceFirewallSettings />} />
								<Route path="/workspace-policies" element={<WorkspacePolicySettings />} />
								<Route path="/workspace-categories" element={<WorkspaceCategorySettings />} />
								<Route path="/workspace-usergroups" element={<WorkspaceUserGroupSettings />} />
								<Route path="/tasks/*" element={<TasksContainer />} />
							</React.Fragment>
						)}

						{((ctx?.isInTenant || ctx?.isInSystem) && !ctx?.isInWorkspace) && (
							<React.Fragment>
								<Route path="/workspaces/*" element={<WorkspacesContainer />} />
								<Route path="/auth/*" element={<AuthSettingsContainer />} />
								<Route path="/email/*" element={<EmailSettingsContainer />} />
								<Route path="/roles/*" element={<RolesSettingsContainer />} />
								<Route path="/users/*" element={<UsersContainer />} />
								<Route path="/firewalls/*" element={<FirewallsContainer />} />
								{ctx?.isInTenant && <Route path="/tasks/*" element={<TasksContainer />} />}
							</React.Fragment>
						)}

						{ctx?.isInSystem && (
							<React.Fragment>
								<Route path="/general" element={
									<div>
										<SystemBaseUrl />
										<AllowSignup />
										<SystemReset />
									</div>
								} />
								<Route path="/licensing/*" element={<SystemLicensingPage />} />
								<Route path="/tasks/*" element={<TasksContainer />} />
							</React.Fragment>
						)}

						<Route path="/accounts/*" element={<AccountsContainer />} />
						<Route path="/tenants/*" element={<TenantsContainer />} />

						<Route path="/*" element={
							<Navigate to={`${ctx?.contextPath}/settings`} replace />
						} />
					</Routes>
				</main>
			</div>
		</div>
	);
}
